import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { OfflineCertificate } from './ksef-qr.types';

const STORAGE_KEY = 'pos_ksef_offline_cert';

/** Minimalny odczyt/zapis — pod Electronem trafia do lokalnej bazy, inaczej do localStorage. */
interface CertificateStoreBridge {
  loadCertificate?(): Promise<OfflineCertificate | null>;
  saveCertificate?(cert: OfflineCertificate | null): Promise<void>;
}

/**
 * Certyfikat KSeF typu Offline na stanowisku sprzedaży.
 *
 * Kasa musi wydrukować KOD II w chwili sprzedaży, także bez łączności — a podpis
 * obejmuje skrót konkretnej faktury, więc nie da się go przygotować wcześniej na
 * serwerze. Klucz prywatny musi więc być na urządzeniu; to świadomy kompromis,
 * wynikający wprost z konstrukcji kodu.
 *
 * Dwie drogi zdobycia certyfikatu:
 *  1. pobranie przy pierwszym logowaniu (zawsze online) — `provision()`,
 *  2. ręczne wgranie na kasie, gdy krok 1 nie został wykonany — `importLocally()`;
 *     taki certyfikat jest odsyłany do systemu przy najbliższym połączeniu,
 *     żeby był dostępny również w App i Mobile.
 */
@Injectable({ providedIn: 'root' })
export class KsefCertificateService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _certificate = signal<OfflineCertificate | null>(null);
  private readonly _pendingRegistration = signal(false);

  readonly certificate = this._certificate.asReadonly();
  /** Certyfikat wgrany lokalnie, który czeka na zapisanie w systemie. */
  readonly pendingRegistration = this._pendingRegistration.asReadonly();

  private get bridge(): CertificateStoreBridge | null {
    const w = globalThis as unknown as { posBridge?: { store?: CertificateStoreBridge } };
    return w.posBridge?.store ?? null;
  }

  /** Wczytuje certyfikat z pamięci urządzenia. Wołane przy starcie aplikacji. */
  async load(): Promise<OfflineCertificate | null> {
    const cert = await this.read();
    this._certificate.set(cert);
    this._pendingRegistration.set(!!cert && !cert.registeredInSystem);
    return cert;
  }

  /**
   * Pobiera certyfikat z systemu i zapisuje lokalnie. Wymaga łączności —
   * wołane po zalogowaniu, które i tak zawsze odbywa się online.
   * Nie nadpisuje certyfikatu wgranego ręcznie, który czeka na rejestrację.
   */
  async provision(companyId: string): Promise<OfflineCertificate | null> {
    if (this._pendingRegistration()) {
      return this._certificate();
    }

    const dto = await firstValueFrom(
      this.http.get<OfflineCertificateDto | null>(
        `${this.config.apiUrl()}/ksef/offline-certificate`,
        { headers: { 'company-id': companyId } },
      ),
    );

    if (!dto?.privateKeyPkcs8Base64) {
      return null;
    }

    const cert: OfflineCertificate = {
      serialNumber: dto.serialNumber,
      certificatePem: dto.certificatePem,
      privateKeyPkcs8Base64: dto.privateKeyPkcs8Base64,
      validTo: dto.validTo,
      source: 'provisioned',
      registeredInSystem: true,
    };

    await this.persist(cert);
    return cert;
  }

  /**
   * Wgranie certyfikatu bezpośrednio na kasie. Zapisujemy go lokalnie od razu,
   * żeby dało się wystawiać offline, i oznaczamy do odesłania do systemu.
   */
  async importLocally(cert: Omit<OfflineCertificate, 'source' | 'registeredInSystem'>): Promise<void> {
    if (!cert.serialNumber?.trim() || !cert.privateKeyPkcs8Base64?.trim()) {
      throw new Error('KSeF: niekompletny certyfikat — wymagany numer seryjny i klucz prywatny');
    }

    await this.persist({ ...cert, source: 'uploaded', registeredInSystem: false });
  }

  /**
   * Odsyła lokalnie wgrany certyfikat do systemu. Wołane przy odzyskaniu
   * łączności; po powodzeniu certyfikat przestaje być „tylko na tej kasie”.
   */
  async registerPending(companyId: string): Promise<boolean> {
    const cert = this._certificate();
    if (!cert || cert.registeredInSystem) {
      return false;
    }

    await firstValueFrom(
      this.http.post(
        `${this.config.apiUrl()}/ksef/offline-certificate`,
        {
          serialNumber: cert.serialNumber,
          certificatePem: cert.certificatePem,
          privateKeyPkcs8Base64: cert.privateKeyPkcs8Base64,
          validTo: cert.validTo,
        },
        { headers: { 'company-id': companyId } },
      ),
    );

    await this.persist({ ...cert, registeredInSystem: true });
    return true;
  }

  /** Usuwa certyfikat z urządzenia — np. przy wylogowaniu kasjera z kasy. */
  async forget(): Promise<void> {
    await this.write(null);
    this._certificate.set(null);
    this._pendingRegistration.set(false);
  }

  private async persist(cert: OfflineCertificate): Promise<void> {
    await this.write(cert);
    this._certificate.set(cert);
    this._pendingRegistration.set(!cert.registeredInSystem);
  }

  private async read(): Promise<OfflineCertificate | null> {
    const bridge = this.bridge;
    if (bridge?.loadCertificate) {
      return (await bridge.loadCertificate()) ?? null;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as OfflineCertificate;
    } catch {
      // Uszkodzony wpis nie może zablokować startu kasy.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  /**
   * Usuwa klucz prywatny z kopii zapisywanej w przeglądarce.
   *
   * Pod Electronem certyfikat trzyma proces główny, zaszyfrowany kluczem systemowym
   * (`posBridge.store.saveCertificate`). Wariant webowy nie ma odpowiednika takiego
   * magazynu: localStorage to zwykły plik w profilu, czytelny dla każdego procesu
   * na koncie użytkownika i dla dowolnego XSS w aplikacji. Klucz podpisujący faktury
   * nie może tam trafić, więc zostaje wyłącznie w pamięci sesji — a po odświeżeniu
   * kasa pobiera go ponownie przy logowaniu (`provision()`, zawsze online).
   */
  private withoutPrivateKey(cert: OfflineCertificate): OfflineCertificate {
    const copy = { ...cert } as Record<string, unknown>;
    delete copy['privateKeyPkcs8Base64'];
    return copy as unknown as OfflineCertificate;
  }

  private async write(cert: OfflineCertificate | null): Promise<void> {
    const bridge = this.bridge;
    if (bridge?.saveCertificate) {
      await bridge.saveCertificate(cert);
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (cert) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.withoutPrivateKey(cert)));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
}

interface OfflineCertificateDto {
  serialNumber: string;
  certificatePem: string;
  privateKeyPkcs8Base64: string;
  validTo: string;
}
